import React, { createContext, useContext, useEffect, useState } from "react";
import { api, storeToken, clearToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);

const USER_CACHE_KEY = "icookgreek_user";

const readCachedUser = () => {
    try {
        const raw = localStorage.getItem(USER_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeCachedUser = (u) => {
    try {
        if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
        else localStorage.removeItem(USER_CACHE_KEY);
    } catch {}
};

export const AuthProvider = ({ children }) => {
    // Eagerly hydrate from cache so navbar doesn't flicker on hard reload
    const [user, setUser] = useState(() => (getToken() ? readCachedUser() : null));
    const [loading, setLoading] = useState(true);

    const bootstrap = async () => {
        const token = getToken();
        if (!token) {
            setUser(null);
            writeCachedUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            writeCachedUser(data);
        } catch {
            clearToken();
            writeCachedUser(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        bootstrap();
    }, []);

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        storeToken(data.access_token);
        setUser(data.user);
        writeCachedUser(data.user);
        return data.user;
    };

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        storeToken(data.access_token);
        setUser(data.user);
        writeCachedUser(data.user);
        return data.user;
    };

    const refreshUser = async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            writeCachedUser(data);
            return data;
        } catch {
            return null;
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        clearToken();
        writeCachedUser(null);
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshUser }}>
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => useContext(AuthCtx);
